import React from "react";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export const BRAND_ACCENTS = {
  GGG: "206 96% 72%",
  MONARCH_ESTATES: "128 66% 73%",
  MONARCH_DESIGN_STUDIO: "255 95% 79%",
  THE_ART_FOREST: "0 100% 83%",
  THE_WISHING_WELL: "48 100% 70%",
};

export const BRAND_NAMES = {
  GGG: "Gopi Gadhvi Group",
  MONARCH_ESTATES: "Monarch Estates",
  MONARCH_DESIGN_STUDIO: "Monarch Design Studio",
  THE_ART_FOREST: "The Art Forest",
  THE_WISHING_WELL: "The Wishing Well",
};

export const brandName = (code) => BRAND_NAMES[code] || code;

export function Panel({ className, children, ...props }) {
  return (
    <div
      className={cn("bg-surface border border-border rounded-md", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function PageHeader({ title, kicker, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        {kicker && (
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-primary mb-1.5">
            {kicker}
          </div>
        )}
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

export function SectionTitle({ children, className }) {
  return (
    <h4 className={cn("text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground", className)}>
      {children}
    </h4>
  );
}

const STATUS_TONES = {
  ok: "text-ok border-ok/40",
  warn: "text-warn border-warn/40",
  err: "text-err border-err/40",
  proc: "text-proc border-proc/40",
  info: "text-ggg border-ggg/40",
  muted: "text-muted-foreground border-border",
  gold: "text-primary border-primary/40",
};

export function StatusBadge({ tone = "muted", children, dot, className }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider bg-transparent",
        STATUS_TONES[tone] || STATUS_TONES.muted,
        className,
      )}
    >
      {dot && <span className={cn("h-1.5 w-1.5 rounded-full bg-current", dot === "pulse" && "pulse-dot")} />}
      {children}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, hint, action }) {
  return (
    <div className="relative overflow-hidden flex flex-col items-start justify-center gap-3 py-14 px-6 border border-dashed border-border rounded-md">
      <img src="/ggg-logo.webp" alt="" aria-hidden="true" className="pointer-events-none absolute -right-5 -bottom-6 h-32 w-auto opacity-[0.05]" />
      {Icon && <Icon className="h-7 w-7 text-muted-foreground/60" strokeWidth={1.5} />}
      <div>
        <div className="text-sm font-medium text-foreground/90">{title}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1 max-w-md leading-relaxed">{hint}</div>}
      </div>
      {action}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("h-4 w-4 animate-spin", className)} />;
}

export function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24 text-muted-foreground gap-2 text-sm">
      <Spinner /> Loading…
    </div>
  );
}

export function ConfigRequired({ label = "CONFIGURATION REQUIRED", hint }) {
  return (
    <div className="inline-flex flex-col gap-1 border border-warn/40 bg-warn/5 rounded-md px-3 py-2">
      <span className="text-[10px] font-mono uppercase tracking-wider text-warn">{label}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

export function Btn({ variant = "primary", size = "md", className, children, ...props }) {
  const variants = {
    primary: "bg-primary text-primary-foreground hover:bg-primary/90 border-transparent",
    outline: "bg-surface border-border text-foreground hover:bg-accent",
    ghost: "bg-transparent border-transparent text-muted-foreground hover:text-foreground hover:bg-accent",
    danger: "bg-transparent border-err/40 text-err hover:bg-err/10",
  };
  const sizes = { sm: "h-8 px-3 text-xs", md: "h-9 px-4 text-sm", icon: "h-9 w-9" };
  return (
    <button
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md border font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none",
        variants[variant], sizes[size], className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

export function TextInput({ className, label, ...props }) {
  return (
    <label className="block">
      {label && <span className="text-xs text-muted-foreground mb-1.5 block">{label}</span>}
      <input
        className={cn(
          "w-full h-9 rounded-md border border-border bg-surface px-3 text-sm placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none",
          className,
        )}
        {...props}
      />
    </label>
  );
}

export function TextArea({ className, label, ...props }) {
  return (
    <label className="block">
      {label && <span className="text-xs text-muted-foreground mb-1.5 block">{label}</span>}
      <textarea
        className={cn(
          "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none resize-y",
          className,
        )}
        {...props}
      />
    </label>
  );
}

export function Stat({ label, value, tone = "foreground", sub }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      <span className={cn("font-head text-2xl font-bold mt-1", tone === "primary" && "text-primary")}>{value}</span>
      {sub && <span className="text-xs text-muted-foreground mt-0.5 font-mono">{sub}</span>}
    </div>
  );
}
